import { useState, useEffect } from 'react';
import { Post } from '../../components/Post/post.ts';
import Card from '../../components/Post/Card.tsx';
import { ArrowPathIcon, ChevronRightIcon } from '@heroicons/react/24/solid';
import DetailedCard from './DetailedCard.tsx';
import { useNavigate, useParams } from 'react-router-dom';
import { fetchUnifiedPosts, getUnifiedPosts } from '../../api/post.ts';
import InstagramIcon from '../../assets/instagram.svg';
import FacebookIcon from '../../assets/facebook.svg';
import Loader from '../../components/Loader.tsx';
import { loadingStages } from './dashboardLoadingStages.tsx';
import { PlatformIcon } from '../../components/PlatformIcons.tsx';
import { useSocial, PlatformType } from '../../context/socialProvider.tsx';

const initialPost: Post = {
  id: '',
  message: '',
  platformPostId: '',
  platformCreatedAt: '',
  mediaUrl: '',
  comments: [],
};

function isErrorWithStatus(
  error: unknown
): error is { status: number; detail?: string } {
  return (
    typeof error === 'object' &&
    error !== null &&
    'status' in error &&
    typeof (error as { status: unknown }).status === 'number'
  );
}

const PostsView = () => {
  const [posts, setPosts] = useState<Post[]>([]);
  const [activePost, setActivePost] = useState<Post>(initialPost);
  const { postID } = useParams();
  const navigate = useNavigate();
  const {
    currentSocial,
    user,
    isLoading: isPlatformLoading,
    error: platformError,
  } = useSocial();

  const [isLoadingPosts, setIsLoadingPosts] = useState(false);

  const selectPost = async (id: string) => {
    const post = posts.findIndex((p) => p.platformPostId === id);
    setActivePost(posts[post]);

    if (currentSocial) {
      navigate(
        `/${currentSocial.id}/${encodeURIComponent(
          currentSocial.providerName
        )}/postsview/${id}`
      );
    } else {
      console.error('No current social account selected');
    }
  };

  // sets activePost from param {postID}
  useEffect(() => {
    if (posts.length > 0 && postID) {
      const postIndex = posts.findIndex((p) => p.platformPostId === postID);
      if (postIndex !== -1) {
        setActivePost(posts[postIndex]);
        console.log('~~~~~activePost~~~~~', posts[postIndex]);
      } else {
        console.log('No post found matching the ID. Handling fallback...');
        navigate(`/${currentSocial?.providerName}/postsview`);
      }
    }
  }, [posts, postID, navigate]);

  const loadPosts = async () => {
    if (!currentSocial) return;

    setIsLoadingPosts(true);
    try {
      console.log('Loading posts for social ID:', currentSocial.id);
      const posts = await getUnifiedPosts(currentSocial.id);
      if (!posts) {
        await fetchUnifiedPosts(
          currentSocial.provider,
          String(currentSocial.id),
          currentSocial.providerId
        );
        if (posts.length === 0) {
          setPosts([]);
        }
      }
      setPosts(posts.result);
    } catch (error) {
      console.error(error);
      // Adjusted error handling to match the provided error structure
      if (isErrorWithStatus(error)) {
        if (error.status === 403) {
          console.log('No connection with Nango. Redirecting to onboarding...');
          navigate('/onboarding');
        }
      }
    } finally {
      setIsLoadingPosts(false);
    }
  };

  useEffect(() => {
    if (user && currentSocial) {
      window.scrollTo(0, 0);
      console.log('User and currentSocial loaded:', user, currentSocial);
      if (Object.keys(user).length > 0 && user.onboarded !== undefined) {
        if (user.onboarded) {
          setPosts([]);
          loadPosts();
        } else {
          console.log(
            'User not onboarded, useeffect redirecting to onboarding...',
            user
          );
          navigate('/onboarding');
        }
      } else {
        console.log('Waiting for user data to fully load...');
      }
    }
  }, [user, currentSocial]);

  return (
    <>
      <div
        className="h-[calc(100vh-var(--header-height,0px))] flex flex-col overflow-hidden"
        ref={(el) => {
          if (el) {
            // Calculate available height by measuring header offset
            const headerHeight = el.getBoundingClientRect().top;
            el.style.setProperty('--header-height', `${headerHeight}px`);
          }
        }}
      >
        {postID ? (
          <DetailedCard
            posts={posts}
            activePost={activePost as Post} // Add 'as Post' to cast activePost as type Post
            currentSocial={currentSocial}
          />
        ) : (
          <div
            className="flex flex-wrap justify-center items-start gap-4 p-4 mx-auto min-h-[75vh] max-w-[1376px] w-full"
            style={{ maxWidth: '1376px' }}
          >
            {posts.length >= 1 ? (
              posts.map((post) => (
                <div className="w-auto" key={post.platformPostId}>
                  <Card
                    img={post.mediaUrl ?? ''}
                    message={post.message}
                    timestamp={post.platformCreatedAt ?? ''}
                    onClick={() => selectPost(post.platformPostId)}
                    key={post.id}
                  />
                </div>
              ))
            ) : isLoadingPosts ? (
              <div role="status" className="m-auto">
                <Loader stages={loadingStages} />
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center w-full mt-24 text-zinc-400">
                <svg
                  className="w-16 h-16 mb-4 text-zinc-500"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={1.5}
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 20.25c4.556 0 8.25-3.694 8.25-8.25S16.556 3.75 12 3.75 3.75 7.444 3.75 12s3.694 8.25 8.25 8.25z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9 10h.008v.008H9V10zm6 0h.008v.008H15V10zm-6 4h6"
                  />
                </svg>
                <span className="text-lg font-semibold mb-2">
                  No posts found
                </span>
                <span className="text-sm text-zinc-500">
                  Try fetching the latest posts or check back later... or
                  perhaps post something ;)
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default PostsView;
